import { ApiError, NetworkError, callFunction, callFunctionGet } from "./client";
import type { MembershipCategory } from "./members";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RECOVERY_REASONS = new Set([
  "device_lost",
  "device_stolen",
  "device_damaged",
  "authenticator_unavailable",
]);

export type MfaRecoveryReasonCode =
  | "device_lost"
  | "device_stolen"
  | "device_damaged"
  | "authenticator_unavailable";

export interface MfaRecoveryRequestInput {
  member_id: string;
  reason_code: MfaRecoveryReasonCode;
  reason_detail?: string | null;
}

export type MfaRecoveryMode = "assisted_in_person" | "independent_approval";

export interface MfaRecoveryRequestResult {
  request_id: string;
  protocol_number: string;
  mode: MfaRecoveryMode;
  status: "recovery_requested" | "recovery_pending";
}

export interface MfaRecoveryTarget {
  member_id: string;
  display_name: string;
  username: string | null;
  membership_category: MembershipCategory;
  status: MfaRecoveryTargetStatus;
}

export type MfaRecoveryTargetStatus = "ativo" | "inativo";

export type MfaRecoveryRequestStatus =
  | "recovery_requested"
  | "recovery_pending"
  | "recovery_processing"
  | "recovery_completed"
  | "recovery_failed"
  | "recovery_expired"
  | "recovery_cancelled";

export interface MfaRecoveryRequestSummary {
  request_id: string;
  target_display_name: string;
  reason_code: MfaRecoveryReasonCode;
  reason_detail: string | null;
  decision_reference: string | null;
  protocol_number?: string;
  mode?: MfaRecoveryMode;
  status: MfaRecoveryRequestStatus;
  requested_at: string;
  request_expires_at: string;
  approved_at: string | null;
  approval_expires_at: string | null;
}

export interface MfaRecoveryPendingItem {
  request_id: string;
  target_member_id: string;
  target_display_name: string;
  target_username: string | null;
  target_category: "efetivo" | "colaborador";
  reason_code: MfaRecoveryReasonCode;
  decision_reference: string | null;
  protocol_number?: string;
  mode?: MfaRecoveryMode;
  requested_by_username: string | null;
  created_at: string;
  request_expires_at: string;
}

export type MfaRecoveryApprovalResult = MfaRecoveryRequestSummary;

export interface MfaRecoveryConsumeResult {
  status: "recovery_processing";
}

function invalidInput(code: "invalid_request" | "invalid_request_id"): never {
  throw new ApiError(422, code, "Dados da recuperação MFA inválidos.");
}

function assertRequestId(requestId: string): void {
  if (typeof requestId !== "string" || !UUID.test(requestId.trim())) {
    invalidInput("invalid_request_id");
  }
}

type RawMfaRecoveryRequestInput = Partial<Record<keyof MfaRecoveryRequestInput, unknown>> & Record<string, unknown>;

function readMemberId(raw: RawMfaRecoveryRequestInput): string {
  const memberId = typeof raw.member_id === "string" ? raw.member_id.trim() : "";
  if (!UUID.test(memberId)) invalidInput("invalid_request");
  return memberId;
}

function readReasonCode(raw: RawMfaRecoveryRequestInput): MfaRecoveryReasonCode {
  const reasonCode = raw.reason_code;
  if (typeof reasonCode !== "string" || !RECOVERY_REASONS.has(reasonCode)) {
    invalidInput("invalid_request");
  }
  return reasonCode as MfaRecoveryReasonCode;
}

function readReasonDetail(raw: RawMfaRecoveryRequestInput): string | null {
  if (raw.reason_detail == null) return null;
  if (typeof raw.reason_detail !== "string") invalidInput("invalid_request");
  const detail = raw.reason_detail.trim();
  if (detail.length === 0 || detail.length > 280 || /[\r\n]/.test(detail)) {
    invalidInput("invalid_request");
  }
  return detail;
}

function normalizeRequestInput(input: MfaRecoveryRequestInput) {
  const raw = input as RawMfaRecoveryRequestInput;
  const allowed = new Set(["member_id", "reason_code", "reason_detail"]);
  if (Object.keys(raw).some((key) => !allowed.has(key))) invalidInput("invalid_request");
  return {
    member_id: readMemberId(raw),
    reason_code: readReasonCode(raw),
    reason_detail: readReasonDetail(raw),
  };
}

async function preserveSanitizedError<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof ApiError || error instanceof NetworkError) {
      throw error;
    }
    throw new Error("Falha ao processar recuperação MFA.");
  }
}

export async function requestMfaRecovery(
  accessToken: string,
  input: MfaRecoveryRequestInput,
): Promise<MfaRecoveryRequestResult> {
  const body = normalizeRequestInput(input);
  return preserveSanitizedError(() =>
    callFunction<typeof body, MfaRecoveryRequestResult>(
      "mfa-recovery-request",
      body,
      { accessToken },
    )
  );
}

export function readMfaRecoveryTarget(
  accessToken: string,
  memberId: string,
): Promise<MfaRecoveryTarget> {
  return preserveSanitizedError(() =>
    callFunction<{ member_id: string }, MfaRecoveryTarget>(
      "mfa-recovery-target-read",
      { member_id: memberId },
      { accessToken },
    )
  );
}

export function searchMfaRecoveryTargets(
  accessToken: string,
  query: string,
): Promise<MfaRecoveryTarget[]> {
  return preserveSanitizedError(() =>
    callFunction<{ query: string }, MfaRecoveryTarget[]>(
      "mfa-recovery-target-read",
      { query },
      { accessToken },
    )
  );
}

export async function readMfaRecoveryRequest(
  accessToken: string,
  requestId: string,
): Promise<MfaRecoveryRequestSummary> {
  assertRequestId(requestId);
  return preserveSanitizedError(() =>
    callFunctionGet<MfaRecoveryRequestSummary>(
      "mfa-recovery-request",
      { request_id: requestId.trim() },
      { accessToken },
    )
  );
}

export async function approveMfaRecovery(
  accessToken: string,
  requestId: string,
): Promise<MfaRecoveryApprovalResult> {
  assertRequestId(requestId);
  return preserveSanitizedError(() =>
    callFunction<{ request_id: string }, MfaRecoveryApprovalResult>(
      "mfa-recovery-approve",
      { request_id: requestId.trim() },
      { accessToken },
    )
  );
}

export function listPendingMfaRecoveryRequests(
  accessToken: string,
): Promise<MfaRecoveryPendingItem[]> {
  return preserveSanitizedError(() =>
    callFunctionGet<MfaRecoveryPendingItem[]>(
      "mfa-recovery-approve",
      {},
      { accessToken },
    )
  );
}

export async function consumeMfaRecovery(accessToken: string): Promise<MfaRecoveryConsumeResult> {
  return preserveSanitizedError(() =>
    callFunction<Record<string, never>, MfaRecoveryConsumeResult>(
      "mfa-recover-factor",
      {},
      { accessToken },
    )
  );
}
